/** Alert tone synthesis via WebAudio. Renders to WAV (base64) for transmission + plays locally. */
export const TONES = {
  two_tone:    { label: "Two-tone (low → high)",  desc: "Classic paging: 1 s low, 1 s high" },
  hi_lo:       { label: "Hi-Lo siren",            desc: "Alternating high/low, 4 cycles" },
  three_beeps: { label: "Three beeps",            desc: "Short attention beeps" },
  long_beep:   { label: "Long beep",              desc: "Single 1.5 s steady tone" },
  warble:      { label: "Warble",                 desc: "Fast oscillating alarm" },
  pulse:       { label: "Pulse",                  desc: "Five rapid pulses" },
};

// segments: [freqHz|0 for silence, durationSec]
function segments(type) {
  switch (type) {
    case "two_tone":    return [[440, 1.0], [0, 0.05], [880, 1.0]];
    case "hi_lo":       return Array.from({ length: 4 }, () => [[960, 0.3], [640, 0.3]]).flat();
    case "three_beeps": return [[1000, 0.18], [0, 0.12], [1000, 0.18], [0, 0.12], [1000, 0.18]];
    case "long_beep":   return [[750, 1.5]];
    case "warble":      return Array.from({ length: 12 }, (_, i) => [i % 2 ? 1200 : 800, 0.1]);
    case "pulse":       return Array.from({ length: 5 }, () => [[1400, 0.08], [0, 0.08]]).flat();
    default:            return [[440, 1.0], [0, 0.05], [880, 1.0]];
  }
}

export function toneDurationMs(type, repeats = 1) {
  const one = segments(type).reduce((s, [, d]) => s + d, 0);
  return Math.round((one * repeats + 0.25 * (repeats - 1)) * 1000);
}

function schedule(ctx, dest, type, repeats, volume) {
  let t = ctx.currentTime + 0.02;
  for (let r = 0; r < repeats; r++) {
    for (const [f, d] of segments(type)) {
      if (f > 0) {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = "sine"; osc.frequency.value = f;
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(volume, t + 0.01);
        g.gain.setValueAtTime(volume, t + d - 0.015);
        g.gain.linearRampToValueAtTime(0, t + d);
        osc.connect(g).connect(dest);
        osc.start(t); osc.stop(t + d);
      }
      t += d;
    }
    t += 0.25;
  }
  return t;
}

export function playTone(type, repeats = 1, volume = 0.8) {
  const AC = window.AudioContext || window.webkitAudioContext;
  const ctx = new AC();
  const end = schedule(ctx, ctx.destination, type, repeats, volume);
  setTimeout(() => ctx.close().catch(() => {}), (end - ctx.currentTime) * 1000 + 200);
}

export async function renderToneWav(type, repeats = 1, volume = 0.8) {
  const rate = 22050;
  const len = Math.ceil((toneDurationMs(type, repeats) / 1000 + 0.1) * rate);
  const ctx = new OfflineAudioContext(1, len, rate);
  schedule(ctx, ctx.destination, type, repeats, volume);
  const buf = await ctx.startRendering();
  return { base64: encodeWav(buf.getChannelData(0), rate), mimeType: "audio/wav", durationMs: toneDurationMs(type, repeats) };
}

function encodeWav(samples, rate) {
  const out = new ArrayBuffer(44 + samples.length * 2);
  const v = new DataView(out);
  const str = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  str(0, "RIFF"); v.setUint32(4, 36 + samples.length * 2, true); str(8, "WAVE"); str(12, "fmt ");
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true); v.setUint32(24, rate, true);
  v.setUint32(28, rate * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true); str(36, "data"); v.setUint32(40, samples.length * 2, true);
  for (let i = 0, o = 44; i < samples.length; i++, o += 2) v.setInt16(o, Math.max(-1, Math.min(1, samples[i])) * 0x7fff, true);
  const bytes = new Uint8Array(out);
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}
